import { Card, CardContent, Stack, Typography } from '@mui/material';

import AvatarImageWrapper from 'kerberus-components/src/Components/Avatars/AvatarImageWrapper';
import { Dashboard, ProfileListUser } from 'kerberus-components/src/Types/AuthServer';
import { UserOnline } from 'kerberus-components/src/Types/WebsocketServer';

import useGetLastSeenOtherUser from '../../../Utils/activity/useGetLastSeenOtherUser';
import getActivityColor from '../../../Utils/getActivityColor';
import { UploadType } from '../../Image/UploadType';

interface RenderGridItemProps {
    infoscreenLayout: Dashboard['socials'];
    onlineUserData?: UserOnline;
    showLocation?: boolean;
    small?: boolean;
    type: string | undefined;
    userData?: ProfileListUser;
}

export function InfoscreenProfileCard({
    infoscreenLayout,
    onlineUserData,
    showLocation,
    small,
    type,
    userData,
}: Readonly<RenderGridItemProps>) {
    const busy = onlineUserData?.status === 'busy';
    const { lastSeenMinutes, lastSeenText } = useGetLastSeenOtherUser({
        areaName: onlineUserData?.poly_name,
        hasLocation: showLocation,
        layerName: onlineUserData?.layer_name,
        timestamp: onlineUserData?.timestamp,
    });

    if (type === 'people') {
        return (
            <Card
                elevation={0}
                sx={{
                    backdropFilter: 'blur(8px)',
                    backgroundColor: 'transparent',
                    border: small ? '2px solid' : '8px solid',
                    borderColor: getActivityColor({ busy, lastSeenMinutes }),
                    borderRadius: small ? 4 : 6,
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    maxHeight: '100%',
                    maxWidth: 500,
                }}
            >
                <CardContent
                    sx={{
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: 3,
                        flex: 1,
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <Stack
                        spacing={small ? 0 : { sm: 2, xs: 1 }}
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flex: 1,
                            height: '100%',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        <Stack sx={{ alignItems: 'center', width: '100%' }}>
                            <AvatarImageWrapper
                                busy={busy}
                                draggable={false}
                                imagePath={userData?.image}
                                imageType={UploadType.Profile}
                                isLoading={false}
                                lastSeenMinutes={lastSeenMinutes}
                                sx={{
                                    aspectRatio: '1/1',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
                                    height: 'auto',
                                    margin: '0 auto',
                                    mb: 0.5,
                                    width: small ? 25 : '50%',
                                }}
                            />
                            <Stack spacing={-0.25}>
                                <Typography
                                    align="center"
                                    sx={{
                                        color: infoscreenLayout?.textColor || '#ffffff',
                                        fontSize: small
                                            ? '6px !important'
                                            : {
                                                  lg: '0.85rem',
                                                  md: '0.5rem',
                                                  xl: '1.1rem',
                                              },
                                        fontWeight: 'bold',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {userData?.first_name}
                                </Typography>
                                <Typography
                                    align="center"
                                    sx={{
                                        color: infoscreenLayout?.textColor || '#ffffff',
                                        fontSize: small
                                            ? '6px !important'
                                            : {
                                                  lg: '0.85rem',
                                                  md: '0.5rem',
                                                  xl: '1.1rem',
                                              },
                                        fontWeight: 'bold',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {userData?.last_name}
                                </Typography>
                            </Stack>
                            <Typography
                                align="center"
                                sx={{
                                    color: infoscreenLayout?.textColor || '#ffffff',
                                    fontSize: small
                                        ? '4px !important'
                                        : {
                                              lg: '0.65rem',
                                              md: '0.35rem',
                                              xl: '1rem',
                                          },
                                    minHeight: small ? undefined : '1.2em',
                                    mt: 0.5,
                                }}
                            >
                                {(infoscreenLayout?.hasUserTitle && userData?.title) || '\u00A0'}
                            </Typography>
                        </Stack>
                        <Typography
                            align="center"
                            sx={{
                                color: infoscreenLayout?.textColor || '#ffffff',
                                fontSize: small
                                    ? '5px !important'
                                    : {
                                          lg: '0.7rem',
                                          md: '0.4rem',
                                          xl: '1.05rem',
                                      },
                                width: '100%',
                            }}
                        >
                            {lastSeenText}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    } else {
        return null;
    }
}
